/**
 * Per-radiologist report templates.
 *
 * Each saved user owns a complete `UserReportTemplate` (sections + an
 * LLM-generated `routingMap`). There is no merge layer — the template either
 * comes from the user's DDB row (if they've saved before) or from the global
 * default (`useTemplate`).
 *
 *   - {@link useUserTemplate}        read the caller's saved template; null if unsaved
 *   - {@link useUserTemplates}       list summaries of all saved templates
 *   - {@link useUpsertUserTemplate}  upsert (POST runs LLM, may take a couple seconds)
 *   - {@link useDeleteUserTemplate}  revert: delete the row, fall back to global
 *   - {@link useEffectiveTemplate}   user's saved template ?? global
 *
 * The editor consumes `useEffectiveTemplate`; the settings page consumes the
 * lower-level hooks so it can branch on "has the user saved yet".
 *
 * Backend: `/api/my-templates` (a2zrai/web-demo-backend, schema changed in
 * the same PR that adds this hook).
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { getApiBaseUrl } from "../config/api";
import { useUserId } from "../context/AuthContext";
import { useTemplate, type FindingSubsectionTemplate, type ReportTemplate } from "./useTemplate";

export interface UserReportTemplate {
  examType: string;
  sections: FindingSubsectionTemplate[];
  /** Map: standard report-section kind (the output of the backend's
   *  get_report_section, e.g. "gallbladder") -> the rad's section kind. Null
   *  when no section receives it; the backend falls back to the global default
   *  at read time. Seeded by the LLM on save and editable by the rad. */
  routingMap: Record<string, string | null>;
  updatedAt?: string;
}

export interface UserTemplateSummary {
  examType: string;
  updatedAt: string;
}

interface UserTemplateListResponse {
  templates: UserTemplateSummary[];
}

interface UpsertUserTemplatePayload {
  examType: string;
  sections: FindingSubsectionTemplate[];
  /** Optional explicit routing (standard section kind -> the rad's section
   *  kind, or null). When provided the backend validates and persists it
   *  verbatim — no LLM call. When omitted, the backend generates it via the
   *  LLM (first save / "Auto-assign with AI"). */
  routingMap?: Record<string, string | null>;
}

export const userTemplateKeys = {
  all: ["user-template"] as const,
  list: (userId: string | null) => [...userTemplateKeys.all, "list", userId ?? ""] as const,
  one: (userId: string | null, examType: string) =>
    [...userTemplateKeys.all, "one", userId ?? "", examType] as const,
};

// ============================================================================
// Fetchers
// ============================================================================

async function fetchUserTemplate(examType: string): Promise<UserReportTemplate | null> {
  const baseUrl = getApiBaseUrl();
  const encoded = encodeURIComponent(examType);
  const response = await axios.get<UserReportTemplate | null>(
    `${baseUrl}/api/my-templates/${encoded}`,
    { withCredentials: true },
  );
  // BE returns the body literally `null` (HTTP 200) when no row exists, so
  // the FE doesn't need a 404-vs-error branch.
  return response.data ?? null;
}

async function fetchUserTemplates(): Promise<UserTemplateSummary[]> {
  const baseUrl = getApiBaseUrl();
  const response = await axios.get<UserTemplateListResponse>(`${baseUrl}/api/my-templates`, {
    withCredentials: true,
  });
  return response.data.templates;
}

async function upsertUserTemplate(payload: UpsertUserTemplatePayload): Promise<UserReportTemplate> {
  const baseUrl = getApiBaseUrl();
  const response = await axios.post<UserReportTemplate>(`${baseUrl}/api/my-templates`, payload, {
    withCredentials: true,
  });
  return response.data;
}

async function deleteUserTemplate(examType: string): Promise<void> {
  const baseUrl = getApiBaseUrl();
  const encoded = encodeURIComponent(examType);
  await axios.delete(`${baseUrl}/api/my-templates/${encoded}`, { withCredentials: true });
}

// ============================================================================
// Read hooks
// ============================================================================

export function useUserTemplate(examType: string | null) {
  const userId = useUserId();
  return useQuery({
    queryKey: userTemplateKeys.one(userId, examType ?? ""),
    queryFn: () => fetchUserTemplate(examType!),
    enabled: !!examType && !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
}

export function useUserTemplates() {
  const userId = useUserId();
  return useQuery({
    queryKey: userTemplateKeys.list(userId),
    queryFn: fetchUserTemplates,
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
}

// ============================================================================
// Mutations
// ============================================================================

export function useUpsertUserTemplate() {
  const queryClient = useQueryClient();
  const userId = useUserId();
  return useMutation({
    mutationFn: upsertUserTemplate,
    onSuccess: (data) => {
      queryClient.setQueryData(userTemplateKeys.one(userId, data.examType), data);
      queryClient.invalidateQueries({ queryKey: userTemplateKeys.list(userId) });
    },
  });
}

export function useDeleteUserTemplate() {
  const queryClient = useQueryClient();
  const userId = useUserId();
  return useMutation({
    mutationFn: deleteUserTemplate,
    onSuccess: (_void, examType) => {
      queryClient.setQueryData(userTemplateKeys.one(userId, examType), null);
      queryClient.invalidateQueries({ queryKey: userTemplateKeys.list(userId) });
    },
  });
}

// ============================================================================
// Combined hook — what the case-viewer editor uses
// ============================================================================

export interface UseEffectiveTemplateResult {
  data: ReportTemplate | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<void>;
}

/** Returns the rad's saved template if present, else the global default.
 *  Same shape as `useTemplate` so call sites can drop-in. */
export function useEffectiveTemplate(examType: string | null): UseEffectiveTemplateResult {
  const global = useTemplate(examType);
  const user = useUserTemplate(examType);

  const data = useMemo<ReportTemplate | undefined>(() => {
    if (user.data) {
      // The user's saved template is a complete ReportTemplate at runtime —
      // the only fields the editor reads are `id`, `version`, `examType`,
      // `negativeImpression`, and `findings`. The wire shape carries `sections`
      // (which IS the editor's `findings`) plus `routingMap` (BE-only at read
      // time). Synthesize a ReportTemplate shape for the editor.
      const fallback = global.data;
      return {
        id: fallback?.id ?? user.data.examType,
        version: fallback?.version ?? "user",
        examType: user.data.examType,
        negativeImpression: fallback?.negativeImpression ?? "",
        findings: user.data.sections,
      };
    }
    return global.data;
  }, [global.data, user.data]);

  const refetch = useCallback(async () => {
    await Promise.all([global.refetch(), user.refetch()]);
  }, [global, user]);

  return {
    data,
    isLoading: global.isLoading || user.isLoading,
    isFetching: global.isFetching || user.isFetching,
    isSuccess: global.isSuccess && user.isSuccess,
    isError: global.isError || user.isError,
    error: global.error ?? user.error,
    refetch,
  };
}

/** Total char count of the effective-template skeleton: negative impression +
 *  each finding's label and default sentence. Used by the audit log to derive
 *  an edit-distance baseline against the signed report HTML. */
export function getTemplateCharCount(template: ReportTemplate | null | undefined): number {
  if (!template) return 0;
  return (
    template.negativeImpression.length +
    template.findings.reduce((acc, f) => acc + f.label.length + f.default.length, 0)
  );
}
